export type DedupeOption =
  | 'ignoreCase'
  | 'ignoreOuterSpaces'
  | 'removeBlankLines'

export interface DedupeOptions {
  ignoreCase: boolean
  ignoreOuterSpaces: boolean
  removeBlankLines: boolean
}

export interface DedupeStats {
  totalLines: number
  uniqueLines: number
  duplicateLines: number
  blankLinesRemoved: number
}

export interface DedupeResult {
  output: string
  stats: DedupeStats
  changed: boolean
}

const BLANK_LINE = /^\p{White_Space}*$/u
const OUTER_WHITESPACE = /^\p{White_Space}+|\p{White_Space}+$/gu

function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n?/g, '\n')
}

function comparisonKey(line: string, options: DedupeOptions): string {
  const compared = options.ignoreOuterSpaces
    ? line.replace(OUTER_WHITESPACE, '')
    : line

  return options.ignoreCase ? compared.toLowerCase() : compared
}

export function dedupeText(text: string, options: DedupeOptions): DedupeResult {
  const normalizedInput = normalizeLineEndings(text)
  const inputLines = text.length === 0 ? [] : normalizedInput.split('\n')
  const seen = new Set<string>()
  const lines: string[] = []
  let duplicateLines = 0
  let blankLinesRemoved = 0

  for (const line of inputLines) {
    if (options.removeBlankLines && BLANK_LINE.test(line)) {
      blankLinesRemoved += 1
      continue
    }

    const key = comparisonKey(line, options)
    if (seen.has(key)) {
      duplicateLines += 1
      continue
    }

    seen.add(key)
    lines.push(line)
  }

  const output = lines.join('\n')

  return {
    output,
    stats: {
      totalLines: inputLines.length,
      uniqueLines: lines.length,
      duplicateLines,
      blankLinesRemoved,
    },
    changed: output !== normalizedInput,
  }
}
